import { useEffect, useState } from 'react';
import type {
  ApiClient, SanitaerBerechnungsbereich, SanitaerBerechnungszeile, SanitaerDruckerhoehung, SanitaerDruckminderer,
  SanitaerDruckmindererVorschlag,
} from '../api';
import {
  ContentSection, DataTable, EmptyState, ErrorState, ExportLeiste, KpiCard, KpiReihe, LoadingState, PropertyGrid,
  StatusBadge, Vorbehalte, formatiereZahl, herkunft, type ExportZiel, type Fachstatus, type Spalte, type Vorbehalt,
} from '../muster';
import { PageHeader } from '../rahmen/PageHeader';
import { Rechenstand, sanitaerFachvorbehalt, zeitpunkt } from './sanitaer/kopf';
import './sanitaer/sanitaer.css';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DRUCKZONEN UND DRUCKERHÖHUNG (Paket TS9b2, TX-STANDARD S1)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Eine eigene Seite für das, was bisher über drei Stellen verteilt war: die
 * Gattungen `druckzone`, `druckerhoehung` und `druckminderer` des
 * Trinkwasserlaufs (auf `sanitaer/trinkwasser` zwischen 350 Netzteilen), die
 * gerechneten Druckerhöhungsanlagen (`/sanitaer/druckerhoehung`, bisher nur auf
 * den Produktdaten) und die Druckminderervorschläge je Zone
 * (`/sanitaer/druckminderer`, bisher ohne Oberfläche).
 *
 * Kein eigener Lauf und keine eigene Mappe: Rechenstand, PDF und Excel sind
 * die des Trinkwasserlaufs - die Trinkwassermappe trägt die Blätter
 * Druckzonen, Druckerhöhung und Druckminderer. Deshalb steht auch der
 * Vorbehalt SAN-05 der Mappe hier. SAN-06 (Ruhedruckgrenze, Lage der
 * Druckminderer) steht NUR auf der Seite: Die Mappe ändert sich nicht.
 *
 * Die Grenze `ruhedruck_max_bar` ist der Teil „Annahmen und Vorgaben“
 * (S1.6): Wie ein Vorschlag zu lesen ist, hängt daran, ob sie geprüft ist.
 */

/** Nur auf der Seite, nicht in der Mappe (wie die Zirkulation auf den Teilstrecken, R5). */
export const VORBEHALT_SAN06 = 'Vorbehalt SAN-06: Der zulässige Ruhedruck an der Entnahmestelle (angesetzt 5 bar nach '
  + 'DIN 1988-200) und die Lage der Druckminderer sind nicht bestätigt. Die Vorschläge sind Mengenangaben auf dieser '
  + 'Grenze.';

/** Die drei Gattungen des Trinkwasserlaufs, die diese Seite zeigt - in dieser Folge. */
export const LAUFGATTUNGEN = ['druckzone', 'druckerhoehung', 'druckminderer'] as const;

const GATTUNGSTITEL: Record<string, string> = {
  druckzone: 'Druckerhöhung je Druckzone',
  druckerhoehung: 'Druckerhöhung — Gesamtnetz',
  druckminderer: 'Druckminderer im Trinkwasserlauf',
};

const OHNE_TRINKWASSER = 'Für Trinkwasser liegt noch keine Berechnung vor.';

interface Eigenschaften {
  api: ApiClient;
  projektId: string;
}

interface Laufstand {
  daten: SanitaerBerechnungsbereich | null;
  zeilen: Record<string, SanitaerBerechnungszeile[]>;
  /** Warum es keinen Lauf gibt (die Meldung des Servers), sonst null. */
  ohne: string | null;
}

function meldung(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** „geprüft“ / „ungeprüft“ - wie die Annahmen der Berechnungsseiten. */
export function pruefstand(geprueft: boolean): Fachstatus {
  return geprueft ? { zustand: 'gut', text: 'geprüft' } : { zustand: 'warnung', text: 'ungeprüft' };
}

/** Die Bewertung einer Zeile des Laufs - wie auf den Berechnungsseiten. */
export function zeilenbewertung(bewertung: string): Fachstatus {
  if (bewertung === 'beanstandet') return { zustand: 'warnung', text: 'beanstandet' };
  if (bewertung === 'hinweis') return { zustand: 'info', text: 'Hinweis' };
  return { zustand: 'gut', text: 'in Ordnung' };
}

function lesbar(name: string): string {
  const aus = name.replace(/_/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  return aus.charAt(0).toUpperCase() + aus.slice(1);
}

function wertText(wert: unknown): string {
  if (wert === null || wert === undefined) return '—';
  if (typeof wert === 'boolean') return wert ? 'ja' : 'nein';
  if (typeof wert === 'number') return formatiereZahl(wert);
  return String(wert);
}

export function SanitaerDruckzonen({ api, projektId }: Eigenschaften) {
  const [lauf, setLauf] = useState<Laufstand | null>(null);
  const [dea, setDea] = useState<SanitaerDruckerhoehung[] | null>(null);
  const [minderer, setMinderer] = useState<SanitaerDruckminderer | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [versuch, setVersuch] = useState(0);

  useEffect(() => {
    let aktiv = true;
    setLauf(null);
    setDea(null);
    setMinderer(null);
    setFehler(null);

    // Der Lauf: je Gattung ein Abruf (der Server filtert), der Kopf aus dem ersten. Ohne Lauf antwortet der Server
    // mit „keine Berechnung“ - das ist kein Seitenfehler, sondern der Stand „noch nicht gerechnet“.
    Promise.all(LAUFGATTUNGEN.map((g) => api.sanitaerBerechnungBereich(projektId, 'trinkwasser', g)))
      .then((b) => {
        if (!aktiv) return;
        const zeilen: Record<string, SanitaerBerechnungszeile[]> = {};
        LAUFGATTUNGEN.forEach((g, i) => { zeilen[g] = b[i].zeilen.filter((z) => z.gattung === g); });
        setLauf({ daten: b[0], zeilen, ohne: null });
      })
      .catch((e) => { if (aktiv) setLauf({ daten: null, zeilen: {}, ohne: meldung(e) }); });

    Promise.all([api.sanitaerDruckerhoehung(projektId), api.sanitaerDruckminderer(projektId)])
      .then(([d, m]) => {
        if (!aktiv) return;
        setDea(d);
        setMinderer(m);
      })
      .catch((e) => { if (aktiv) setFehler(meldung(e)); });

    return () => { aktiv = false; };
  }, [api, projektId, versuch]);

  const laufDaten = lauf?.daten ?? null;
  const ohneLauf = laufDaten ? undefined : { grund: OHNE_TRINKWASSER };
  const ziele: ExportZiel[] = [
    {
      art: 'pdf', beschriftung: 'PDF Bericht', laden: () => api.sanitaerBerichtPdf(projektId, 'trinkwasser'),
      hinweis: 'Der Trinkwasserbericht (TWB).', gesperrt: ohneLauf,
    },
    {
      art: 'excel', beschriftung: 'Excel', laden: () => api.sanitaerBerechnungExcel(projektId, 'trinkwasser'),
      hinweis: 'Die Trinkwassermappe mit den Blättern Druckzonen, Druckerhöhung und Druckminderer.', gesperrt: ohneLauf,
    },
  ];

  const kopf = (
    <PageHeader
      titel="Druckzonen und Druckerhöhung"
      beschreibung={'Druckzonen, Druckerhöhungsanlagen und Druckminderer des Trinkwassernetzes - aus dem '
        + 'Trinkwasserlauf, mit der Ruhedruckgrenze, auf der die Vorschläge stehen.'}
      meta={lauf ? <Rechenstand lauf={laufDaten?.lauf} veraltet={laufDaten?.lauf.standVeraltet} /> : undefined}
      aktionen={<ExportLeiste ziele={ziele} />}
    />
  );

  if (fehler) {
    return (
      <>
        {kopf}
        <ErrorState
          meldung="Druckerhöhung und Druckminderer konnten nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={() => setVersuch((v) => v + 1)}
        />
      </>
    );
  }

  if (!lauf || !dea || !minderer) {
    return <>{kopf}<LoadingState text="Druckzonen und Druckerhöhung werden abgerufen …" zeilen={8} spalten={6} /></>;
  }

  const vorbehalte: Vorbehalt[] = [];
  const frage = sanitaerFachvorbehalt('trinkwasser');
  if (frage) vorbehalte.push(frage);
  vorbehalte.push({ art: 'fachfrage', text: VORBEHALT_SAN06, verweis: 'Annahmen und Vorgaben', ziel: '#druck-annahmen' });
  if (laufDaten?.lauf.bemessungGesperrt) {
    vorbehalte.push({
      art: 'sonstiges',
      text: `Zwischenstand, keine Bemessung (doppelte Verknüpfung): ${laufDaten.lauf.sperrgrund ?? ''} Die Zahlen `
        + 'des Laufs sind nicht entdoppelt.',
    });
  }
  if (laufDaten?.lauf.standVeraltet) {
    vorbehalte.push({
      art: 'veraltet',
      text: `Gerechnet am ${zeitpunkt(laufDaten.lauf.gerechnetAm)}, Sanitäraufnahme vom `
        + `${zeitpunkt(laufDaten.lauf.modellstand)}. Jede Zahl des Laufs gilt für einen Modellstand, den es nicht mehr `
        + 'gibt. Über „Alle Bereiche rechnen“ auf der Bestandsseite entsteht der neue Stand.',
    });
  }

  const zonen = lauf.zeilen.druckzone ?? [];
  const ueber = minderer.entnahmestellenUeberGrenze;

  return (
    <>
      {kopf}
      <Vorbehalte eintraege={vorbehalte} />

      <KpiReihe>
        <KpiCard titel="Druckzonen im Lauf" wert={laufDaten ? zonen.length : null} />
        <KpiCard titel="Druckerhöhungsanlagen" wert={dea.length} />
        <KpiCard
          titel="Entnahmestellen über der Grenze"
          wert={ueber}
          vergleich={`von ${formatiereZahl(minderer.entnahmestellen, 0)} Entnahmestellen`}
          zustand={ueber > 0 ? 'warnung' : 'gut'}
          zustandText={ueber > 0 ? 'Druckminderer nötig' : 'keine über der Grenze'}
        />
      </KpiReihe>

      <Grenze minderer={minderer} />

      {laufDaten ? LAUFGATTUNGEN.map((g) => (
        <Laufzeilen key={g} titel={GATTUNGSTITEL[g]} gattung={g} zeilen={lauf.zeilen[g] ?? []} />
      )) : (
        <EmptyState
          titel="Der Trinkwasserlauf ist noch nicht gerechnet."
          erklaerung={'Druckzonen und die Druckerhöhung je Zone entstehen mit dem Trinkwasserlauf über „Alle '
            + 'Bereiche rechnen“ auf der Bestandsseite.'}
          aktion={<ErrorState klein meldung={lauf.ohne ?? OHNE_TRINKWASSER} />}
        />
      )}

      <Druckerhoehungsanlagen anlagen={dea} />
      <Vorschlaege vorschlaege={minderer.vorschlaege} />
    </>
  );
}

/** Die Ruhedruckgrenze als Vorgabe mit Herkunft und Prüfstand (S1.6). */
function Grenze({ minderer }: { minderer: SanitaerDruckminderer }) {
  const g = minderer.grenze;
  return (
    <ContentSection
      id="druck-annahmen"
      titel="Annahmen und Vorgaben"
      beschreibung={'Die obere Druckgrenze an der Entnahmestelle. Aus ihr folgen die Druckminderervorschläge - ist sie '
        + 'ungeprüft, sind es auch die Vorschläge.'}
      meta={g ? <StatusBadge {...pruefstand(g.geprueft)} klein /> : undefined}
    >
      {g ? (
        <PropertyGrid
          spalten={2}
          eintraege={[
            { beschriftung: 'Ruhedruck höchstens', wert: g.wert, einheit: g.einheit ?? undefined, nachkomma: 1 },
            { beschriftung: 'Herkunft', wert: <StatusBadge {...herkunft(g.herkunft)} klein /> },
            { beschriftung: 'Norm', wert: g.norm },
            { beschriftung: 'Prüfstand', wert: <StatusBadge {...pruefstand(g.geprueft)} klein /> },
            { beschriftung: 'Begründung', wert: g.begruendung, breit: true },
          ]}
        />
      ) : (
        <EmptyState
          klein
          titel="Die Ruhedruckgrenze ist nicht gepflegt."
          erklaerung="Ohne die Annahme ruhedruck_max_bar gibt es keine Druckminderervorschläge."
        />
      )}
    </ContentSection>
  );
}

/**
 * Die Zeilen einer Gattung des Laufs. Die Spalten kommen aus den Werten der ersten Zeile (alle Zeilen einer Gattung
 * stammen aus demselben Datensatz); der maßgebende und die beanstandeten stehen oben.
 */
export function Laufzeilen({ titel, gattung, zeilen }: {
  titel: string; gattung: string; zeilen: SanitaerBerechnungszeile[];
}) {
  const sortiert = zeilen.slice().sort((a, b) => {
    if (a.istMassgebend !== b.istMassgebend) return a.istMassgebend ? -1 : 1;
    const rang = (z: SanitaerBerechnungszeile) => (z.bewertung === 'beanstandet' ? 0 : z.bewertung === 'hinweis' ? 1 : 2);
    return rang(a) - rang(b) || a.ordnung - b.ordnung;
  });
  const werteschluessel = Object.entries(sortiert[0]?.werte ?? {})
    .filter(([, v]) => v === null || typeof v !== 'object')
    .map(([k]) => k);

  const spalten: Spalte<SanitaerBerechnungszeile>[] = [
    {
      schluessel: 'bewertung', titel: 'Bewertung', art: 'status', breite: '150px',
      wert: (z) => zeilenbewertung(z.bewertung).text,
      inhalt: (z) => <StatusBadge {...zeilenbewertung(z.bewertung)} klein />,
    },
    {
      schluessel: 'bezeichnung', titel: 'Bezeichnung', immerSichtbar: true,
      wert: (z) => z.bezeichnung,
      inhalt: (z) => (
        <>
          {z.istMassgebend && <StatusBadge zustand="info" text="maßgebend" klein />}
          {' '}{z.bezeichnung}
        </>
      ),
    },
    { schluessel: 'kennwert', titel: 'Kennwert', art: 'zahl', wert: (z) => z.kennwert },
    ...werteschluessel.map((k): Spalte<SanitaerBerechnungszeile> => {
      const beschriftung = lesbar(k);
      const treffer = /^(.*?)\s*\[(.+)\]$/.exec(beschriftung);
      return {
        schluessel: `w_${k}`,
        titel: treffer ? treffer[1] : beschriftung,
        einheit: treffer ? treffer[2] : undefined,
        ausrichtung: 'rechts',
        wert: (z) => z.werte?.[k],
        inhalt: (z) => <>{wertText(z.werte?.[k])}</>,
      };
    }),
    { schluessel: 'befund', titel: 'Befund', wert: (z) => z.befund ?? '' },
  ];

  return (
    <ContentSection titel={titel} meta={`${formatiereZahl(zeilen.length, 0)} Zeilen`} nackt>
      <DataTable
        name={titel}
        spalten={spalten}
        zeilen={sortiert}
        schluessel={(z) => z.id}
        ausfuhr
        ausfuhrDateiname={`sanitaer-trinkwasser-${gattung}`}
        dicht
        leer={<EmptyState klein titel="Der Lauf hat dafür keine Zeilen erzeugt." />}
      />
    </ContentSection>
  );
}

function Druckerhoehungsanlagen({ anlagen }: { anlagen: SanitaerDruckerhoehung[] }) {
  const spalten: Spalte<SanitaerDruckerhoehung>[] = [
    { schluessel: 'kennung', titel: 'Kennung', immerSichtbar: true, wert: (a) => a.kennung },
    { schluessel: 'zone', titel: 'Zone', wert: (a) => a.bezeichnung || a.zone },
    {
      schluessel: 'pumpen', titel: 'Pumpen', wert: (a) => a.pumpen,
      inhalt: (a) => (a.pumpen == null ? <>—</> : (
        <>
          {formatiereZahl(a.pumpen, 0)}
          {a.betriebspumpen != null && (
            <span className="schwach">
              {' '}({formatiereZahl(a.betriebspumpen, 0)} Betrieb + {formatiereZahl(a.pumpen - a.betriebspumpen, 0)} Reserve)
            </span>
          )}
        </>
      )),
    },
    { schluessel: 'spitze', titel: 'Spitzendurchfluss', art: 'zahl', einheit: 'l/s', nachkomma: 2, wert: (a) => a.spitzeLs },
    {
      schluessel: 'foerderstrom', titel: 'Förderstrom', art: 'zahl', einheit: 'm³/h', nachkomma: 1,
      wert: (a) => a.foerderstromM3h,
    },
    { schluessel: 'foerderhoehe', titel: 'Förderhöhe', art: 'zahl', einheit: 'm', nachkomma: 1, wert: (a) => a.foerderhoeheM },
    { schluessel: 'erforderlich', titel: 'erforderlich', art: 'zahl', einheit: 'm', nachkomma: 1, wert: (a) => a.erforderlichM },
    {
      schluessel: 'versorgung', titel: 'Versorgungsdruck', art: 'zahl', einheit: 'm', nachkomma: 1,
      wert: (a) => a.versorgungsdruckM,
    },
    {
      schluessel: 'herkunft', titel: 'Herkunft', art: 'status',
      wert: (a) => herkunft(a.herkunft).text,
      inhalt: (a) => <StatusBadge {...herkunft(a.herkunft)} klein />,
    },
    { schluessel: 'befund', titel: 'Befund', wert: (a) => a.befund ?? '' },
  ];

  return (
    <ContentSection
      titel="Druckerhöhungsanlagen"
      meta={`${formatiereZahl(anlagen.length, 0)} Anlagen`}
      beschreibung={'Nicht modelliert, sondern gerechnet: Die Förderhöhe ist der Fehlbetrag, den der Versorgungsdruck '
        + 'nicht trägt (geodätische Höhe, Mindestfließdruck, Reibung, Armaturen).'}
      nackt
    >
      <DataTable
        name="Druckerhöhungsanlagen"
        spalten={spalten}
        zeilen={anlagen}
        schluessel={(a) => a.kennung}
        ausfuhr
        ausfuhrDateiname="sanitaer-druckerhoehungsanlagen"
        dicht
        leer={<EmptyState klein titel="Es ist keine Druckerhöhungsanlage gerechnet." />}
      />
    </ContentSection>
  );
}

function Vorschlaege({ vorschlaege }: { vorschlaege: SanitaerDruckmindererVorschlag[] }) {
  const spalten: Spalte<SanitaerDruckmindererVorschlag>[] = [
    { schluessel: 'zone', titel: 'Zone', immerSichtbar: true, wert: (v) => v.zonenbezeichnung || v.zone },
    { schluessel: 'einbauort', titel: 'Einbauort', wert: (v) => v.einbauort },
    { schluessel: 'eingang', titel: 'Eingangsdruck', art: 'zahl', einheit: 'bar', nachkomma: 2, wert: (v) => v.eingangsdruckBar },
    { schluessel: 'ausgang', titel: 'Ausgangsdruck', art: 'zahl', einheit: 'bar', nachkomma: 2, wert: (v) => v.ausgangsdruckBar },
    { schluessel: 'nennweite', titel: 'Nennweite (DN)', art: 'zahl', wert: (v) => v.nennweite },
    {
      schluessel: 'ueber', titel: 'über der Grenze', art: 'zahl', wert: (v) => v.entnahmestellenUeberGrenze,
      inhalt: (v) => (
        <>
          {formatiereZahl(v.entnahmestellenUeberGrenze, 0)} von {formatiereZahl(v.entnahmestellenGesamt, 0)}
        </>
      ),
    },
    {
      schluessel: 'hoechster', titel: 'höchster Ruhedruck', art: 'zahl', einheit: 'bar', nachkomma: 2,
      wert: (v) => v.hoechsterRuhedruckBar,
    },
    {
      schluessel: 'herkunft', titel: 'Herkunft', art: 'status',
      wert: (v) => herkunft(v.herkunft).text,
      inhalt: (v) => <StatusBadge {...herkunft(v.herkunft)} klein />,
    },
    {
      schluessel: 'pruefstand', titel: 'Prüfstand', art: 'status',
      wert: (v) => pruefstand(v.geprueft).text,
      inhalt: (v) => <StatusBadge {...pruefstand(v.geprueft)} klein />,
    },
    { schluessel: 'berechnet', titel: 'berechnet', wert: (v) => zeitpunkt(v.berechnetAm) },
  ];

  return (
    <ContentSection
      titel="Druckminderer"
      meta={`${formatiereZahl(vorschlaege.length, 0)} Vorschläge`}
      beschreibung={'Je Zone ein Vorschlag mit Einbauort, Einstellung und Nennweite - eine Mengenangabe für die '
        + 'Bestellung, auf der Ruhedruckgrenze oben.'}
      nackt
    >
      <DataTable
        name="Druckminderer"
        spalten={spalten}
        zeilen={vorschlaege}
        schluessel={(v) => v.id}
        ausfuhr
        ausfuhrDateiname="sanitaer-druckminderer"
        dicht
        leer={<EmptyState klein titel="Keine Zone braucht einen Druckminderer." />}
      />
    </ContentSection>
  );
}
